// src/pages/Admin/Testimonials/AdminTestimonials.tsx
import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import siteContentData from '../../../siteContentData.json';

interface TestimonialItem {
  _id: string;
  id: string;
  author: string;
  feedback?: string;
  rating?: number;
  product?: string;
  role?: string;
  content?: string;
  __v: number;
}

const AdminTestimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<TestimonialItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadTestimonials = () => {
      try {
        setIsLoading(true);
        // Combine both testimonials and homeTestimonials from the JSON file
        const allTestimonials = [
          ...siteContentData.testimonials,
          ...siteContentData.homeTestimonials.map(t => ({
            ...t,
            author: t.name,
            feedback: t.content // Map content to feedback for consistency
          }))
        ];
        setTestimonials(allTestimonials);
      } catch (error) {
        console.error('Error loading testimonials:', error);
        toast.error('Failed to load testimonials data.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTestimonials();
  }, []);

  const handleTestimonialChange = (index: number, field: keyof TestimonialItem, value: string | number) => {
    const updatedTestimonials = [...testimonials];
    updatedTestimonials[index] = {
      ...updatedTestimonials[index],
      [field]: value
    };
    setTestimonials(updatedTestimonials);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Simulate API call to save data
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Testimonials updated successfully!');
    } catch (error) {
      console.error('Error updating testimonials:', error);
      toast.error('Failed to update testimonials.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Manage Testimonials</h1>
        
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={testimonial._id} className="border border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-4">Testimonial {index + 1}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label htmlFor={`author-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Author
                    </label>
                    <input
                      id={`author-${index}`}
                      type="text"
                      value={testimonial.author}
                      onChange={(e) => handleTestimonialChange(index, 'author', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                    />
                  </div>

                  {testimonial.role && (
                    <div className="mb-4">
                      <label htmlFor={`role-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <input
                        id={`role-${index}`}
                        type="text"
                        value={testimonial.role}
                        onChange={(e) => handleTestimonialChange(index, 'role', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                      />
                    </div>
                  )}

                  {testimonial.product && (
                    <div className="mb-4">
                      <label htmlFor={`product-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Product
                      </label>
                      <input
                        id={`product-${index}`}
                        type="text"
                        value={testimonial.product}
                        onChange={(e) => handleTestimonialChange(index, 'product', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                      />
                    </div>
                  )}

                  {testimonial.rating !== undefined && (
                    <div className="mb-4">
                      <label htmlFor={`rating-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Rating (1-5)
                      </label>
                      <input
                        id={`rating-${index}`}
                        type="number"
                        min="1"
                        max="5"
                        value={testimonial.rating}
                        onChange={(e) => handleTestimonialChange(index, 'rating', parseInt(e.target.value))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                      />
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label htmlFor={`content-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                    {testimonial.feedback ? 'Feedback' : 'Content'}
                  </label>
                  <textarea
                    id={`content-${index}`}
                    value={testimonial.feedback || testimonial.content || ''}
                    onChange={(e) => handleTestimonialChange(index, testimonial.feedback ? 'feedback' : 'content', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6">
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#FFB639] hover:bg-[#e5a333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB639] disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AdminTestimonials;